import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import Modal from 'utils/components/modal';
import ConfirmModal from 'app/scripts/components/modals/confirmation-modal';
import Icon from 'utils/components/icon';
import styled, { css } from 'styled-components';
import { foxtrotGreyColor } from 'styled-ui-kit/colors';
import { snakeCase } from 'lodash-es';
import DropdownIcon from '../dropdown-icon';
import { UserRole } from '../../moderation-v2/users/state/types';
import RoleSelector from './role-selector';

const Badges = styled.span`
  ${p =>
    p.hasItems &&
    css`
      border-right: 1px solid ${foxtrotGreyColor};
      display: flex;
      align-items: center;
      padding: 6px 15px 6px 0px;
    `};
`;

const UserBadge = styled.span`
  border-radius: 4px;
  color: white;
  font-size: 11px;
  line-height: 16px;
  display: inline-block;
  padding: 0px 4px;
  margin-left: 7px;
  background-color: ${p => {
    return {
      moderator: '#DF3331',
      viewer: '#373E44',
      journalist: '#FA7811',
      owner: '#2D7BE5',
      'community-moderator': '#000'
    }[p.type];
  }};
`;

class TeammatesControls extends Component {
  static propTypes = {
    spotId: PropTypes.string,
    user: PropTypes.object,
    lastElement: PropTypes.bool,
    isCurrentUser: PropTypes.bool,
    addRole: PropTypes.func,
    removeRole: PropTypes.func
  };

  static defaultProps = {};

  static displayName = 'TeammatesControls';

  static contextTypes = {
    executeAction: PropTypes.func.isRequired
  };

  @autobind
  openUnassignConfirmModal() {
    this.refs.unassignModal.show();
  }

  @autobind
  closeUnassignConfirmModal() {
    this.refs.unassignModal.hide();
  }

  @autobind
  openUnverifyConfirmModal() {
    this.refs.unverifyModal.show();
  }

  closeUnverifyConfirmModal() {
    this.refs.unverifyModal.hide();
  }

  scrollToBottom = () => {
    const { lastElement } = this.props;

    if (!lastElement) {
      return;
    }

    // we must use setTimeout to let the menu opens before the scrolling
    setTimeout(() => {
      let teammatesUsersElement = document.querySelector('.teammates-users');
      teammatesUsersElement.scrollTop =
        teammatesUsersElement.scrollHeight - teammatesUsersElement.clientHeight;
    }, 1);
  };

  @autobind
  handleRoleChange(roles) {
    var { user } = this.props;
    const userId = user.id;

    const [rolesToAssign, rolesToUnassign] = Object.keys(roles || {}).reduce(
      ([assignList, unassignList], curr) => {
        const formattedRole = snakeCase(curr);
        return roles[curr]
          ? [[...assignList, formattedRole], unassignList]
          : [assignList, [...unassignList, formattedRole]];
      },
      [[], []]
    );

    this.props.removeRole({
      userId,
      roles: rolesToUnassign
    });

    this.props.addRole({
      userId,
      roles: rolesToAssign
    });

    this.refs.roleSelector.hide();
  }

  @autobind
  handleRemoveFromTeam() {
    let { user, spotId } = this.props;

    this.props.removeRole({
      userId: user.id,
      roles: user
        .get('roles', [])
        .filter(role => UserRole[role.toUpperCase()] >= UserRole.TEAM_MEMBER)
    });

    this.refs.roleSelector.hide();
  }

  @autobind
  renderUserTypeTags(user) {
    let badges = [];
    if (user.type === 'admin') {
      badges.push(
        <UserBadge key="owner" type="owner">
          Admin
        </UserBadge>
      );
    } else if (user.type === 'moderator') {
      badges.push(
        <UserBadge key="moderator" type="moderator">
          Moderator
        </UserBadge>
      );
    }

    if (user.type === 'journalist') {
      badges.push(
        <UserBadge key="journalist" type="journalist">
          Journalist
        </UserBadge>
      );
    }

    if (user.type === 'moderation_viewer') {
      badges.push(
        <UserBadge key="viewer" type="viewer">
          Viewer
        </UserBadge>
      );
    }

    if (user.type === 'community_moderator') {
      badges.push(
        <UserBadge key="community-moderator" type="community-moderator">
          Community Moderator
        </UserBadge>
      );
    }

    return <Badges hasItems={!!badges.length}>{badges}</Badges>;
  }

  render() {
    let { user, isCurrentUser } = this.props;
    let className = 'role-settings';

    return (
      <div className="teammates-controls">
        {this.renderUserTypeTags(user)}
        {!isCurrentUser && (
          <div>
            <DropdownIcon
              ref="roleSelector"
              caret
              className={className}
              icon={
                <div className="icon-wrapper">
                  <Icon name="settings" className="icon-settings" />
                </div>
              }
              onClick={this.scrollToBottom}
              squeezeBelow
            >
              <RoleSelector
                user={user}
                onChange={this.handleRoleChange}
                onRemoveFromTeam={this.handleRemoveFromTeam}
              />
            </DropdownIcon>
            <Modal ref="unassignModal" className="confirm-modal">
              <ConfirmModal
                onClose={this.closeUnassignConfirmModal}
                title="Confirm Action"
                onConfirm={this.unassign}
                description="Unassigning an admin or moderator will revoke his access to moderation dashboard."
                buttonName="Unassign"
              />
            </Modal>
            <Modal ref="unverifyModal">
              <ConfirmModal
                onClose={this.closeUnverifyConfirmModal}
                title="Confirm Action"
                onConfirm={this.unverify}
                description="Resetting verification will require admin or moderator to re-verify his identity."
                buttonName="Reset"
              />
            </Modal>
          </div>
        )}
      </div>
    );
  }
}

export default TeammatesControls;
